import React, { FC, useState } from 'react';
import { 
  Search, 
  Filter, 
  Plus, 
  Edit2, 
  Trash2, 
  User, 
  Mail, 
  Phone, 
  Globe, 
  Layers, 
  Star,
  X
} from 'lucide-react';
import { Restaurant, PlanType } from '../types';

interface RestaurantsViewProps {
  restaurants: Restaurant[];
  plansArr: any[];
  onAdd: (data: Omit<Restaurant, 'id' | 'createdAt' | 'scansThisMonth' | 'totalMenuItems' | 'rating' | 'revenueThisMonth' | 'qrCodeUrl'>) => void;
  onUpdate: (id: string, data: Partial<Restaurant>) => void;
  onDelete: (id: string) => void;
}

export const RestaurantsView: FC<RestaurantsViewProps> = ({ 
  restaurants, 
  plansArr, 
  onAdd, 
  onUpdate, 
  onDelete 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  
  // Modals / Form States
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRest, setEditingRest] = useState<Restaurant | null>(null);

  // New Restaurant Form State
  const [newRest, setNewRest] = useState({
    name: '',
    cuisine: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    plan: 'Standard' as PlanType,
    customDomain: '',
  });

  // Filter & Search Logic
  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = 
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.ownerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.ownerEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.cuisine.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPlan = planFilter === 'All' || r.plan === planFilter;
    const matchesStatus = statusFilter === 'All' || r.status === statusFilter;

    return matchesSearch && matchesPlan && matchesStatus;
  });

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRest.name || !newRest.ownerName || !newRest.ownerEmail) return;
    onAdd(newRest);
    setNewRest({
      name: '',
      cuisine: '',
      ownerName: '',
      ownerEmail: '',
      ownerPhone: '',
      plan: 'Standard',
      customDomain: '',
    });
    setIsAddOpen(false);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRest) return;
    onUpdate(editingRest.id, editingRest);
    setIsEditOpen(false);
    setEditingRest(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* View Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display font-medium text-2xl tracking-tight text-slate-900">
            Qoşulmuş Restoranların Reyestri
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Abunəlikləri, skan limitlərini və fərdi domenləri vahid idarəetmə panelindən idarə edin.
          </p>
        </div>
        <button
          id="btn-add-restaurant"
          onClick={() => setIsAddOpen(true)}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm select-none cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Restoran Qoş</span>
        </button>
      </div>

      {/* Analytical Badges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200/60 shadow-xs">
          <p className="text-[10px] text-slate-400 font-mono uppercase font-bold">Sınaq müddəti</p>
          <p className="text-2xl font-display font-extrabold text-amber-600 mt-1">
            {restaurants.filter(r => r.status === 'trial').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200/60 shadow-xs">
          <p className="text-[10px] text-slate-400 font-mono uppercase font-bold">Aktiv Hesablar</p>
          <p className="text-2xl font-display font-extrabold text-emerald-600 mt-1">
            {restaurants.filter(r => r.status === 'active').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200/60 shadow-xs">
          <p className="text-[10px] text-slate-400 font-mono uppercase font-bold">Dondurulub / Churn</p>
          <p className="text-2xl font-display font-extrabold text-slate-500 mt-1">
            {restaurants.filter(r => r.status === 'suspended').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200/60 shadow-xs">
          <p className="text-[10px] text-slate-400 font-mono uppercase font-bold">Fərdi domenləri olanlar</p>
          <p className="text-2xl font-display font-extrabold text-indigo-600 mt-1">
            {restaurants.filter(r => r.customDomain).length}
          </p>
        </div>
      </div>

      {/* Control Filters Area */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            id="search-restaurant-input"
            type="text"
            placeholder="Ad, mətbəx şöbəsi, sahib və ya e-mail üzrə axtarış..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden bg-slate-50/50"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          {/* Plan Filter */}
          <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200/80">
            <Layers className="w-3.5 h-3.5 text-slate-500" />
            <select
              id="plan-filter-select"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="bg-transparent text-xs text-slate-650 font-bold focus:outline-hidden cursor-pointer"
            >
              <option value="All">Bütün tariflər</option>
              <option value="Free">Free</option>
              <option value="Standard">Standard</option>
              <option value="Premium">Premium</option>
              <option value="Enterprise">Enterprise</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200/80">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <select
              id="status-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent text-xs text-slate-650 font-bold focus:outline-hidden cursor-pointer"
            >
              <option value="All">Bütün statuslar</option>
              <option value="active">Aktiv</option>
              <option value="trial">Sınaq</option>
              <option value="suspended">Dondurulub</option>
            </select>
          </div>

          {/* Total Matching Badge */}
          <span className="text-[11px] font-mono font-bold bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md ml-auto md:ml-0 border border-indigo-200/30">
            Tapılan restoran: {filteredRestaurants.length}
          </span>
        </div>
      </div>

      {/* Grid of Restaurant Cards */}
      {filteredRestaurants.length === 0 ? (
        <div className="bg-white rounded-2xl py-12 border border-slate-200 text-center space-y-3">
          <p className="text-slate-400 text-sm">Axtarış meyarlarına uyğun restoran tapılmadı.</p>
          <button 
            type="button"
            onClick={() => { setSearchQuery(''); setPlanFilter('All'); setStatusFilter('All'); }}
            className="text-indigo-600 text-xs font-bold underline cursor-pointer"
          >
            Filtrləri sıfırla
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRestaurants.map((rest) => {
            const planBadgeColor = 
              rest.plan === 'Enterprise' ? 'bg-purple-100 text-purple-700 border-purple-200' :
              rest.plan === 'Premium' ? 'bg-indigo-100 text-indigo-700 border-indigo-200' :
              rest.plan === 'Standard' ? 'bg-blue-100 text-blue-700 border-blue-200' :
              'bg-slate-100 text-slate-600 border-slate-200';

            const statusBadgeColor =
              rest.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              rest.status === 'trial' ? 'bg-amber-50 text-amber-700 border-amber-200' :
              'bg-slate-100 text-slate-650 border-slate-200';

            return (
              <div 
                key={rest.id} 
                className="bg-white rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between overflow-hidden relative group"
              >
                {/* Visual Accent top bar depending on subscription */}
                <span className={`h-1.5 w-full block ${
                  rest.status === 'suspended' ? 'bg-slate-400' :
                  rest.plan === 'Enterprise' ? 'bg-purple-600' :
                  rest.plan === 'Premium' ? 'bg-indigo-600' :
                  rest.plan === 'Standard' ? 'bg-blue-600' :
                  'bg-amber-500'
                }`} />

                {/* Main Content Card */}
                <div className="p-5 space-y-4 flex-1">
                  {/* Top line with plan and states status */}
                  <div className="flex justify-between items-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${planBadgeColor}`}>
                      {rest.plan}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusBadgeColor}`}>
                      {rest.status === 'active' ? 'Aktiv' : rest.status === 'trial' ? 'Sınaq müddəti' : 'Dondurulub'}
                    </span>
                  </div>

                  {/* Restaurant Name and Cuisine type */}
                  <div>
                    <h3 className="font-display font-extrabold text-lg text-slate-900 leading-snug group-hover:text-indigo-600 transition-colors">
                      {rest.name}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                      <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 font-medium">
                        {rest.cuisine} mətbəxi
                      </span>
                      <span>·</span>
                      <span className="flex items-center text-amber-600 font-bold">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500 mr-0.5" />
                        {rest.rating}
                      </span>
                    </div>
                  </div>

                  {/* Owner metadata panel */}
                  <div className="bg-slate-50 p-3 rounded-xl space-y-2 text-xs border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-700 font-bold">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      <span className="truncate">{rest.ownerName}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500 font-mono text-[11px]">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      <span className="truncate">{rest.ownerEmail}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500 font-mono text-[11px]">
                      <Phone className="w-3.5 h-3.5 text-slate-400" />
                      <span>{rest.ownerPhone || 'Telefon qeyd edilməyib'}</span>
                    </div>
                  </div>

                  {/* Usage limits of system */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-indigo-50/20 p-2.5 rounded-xl border border-indigo-100/30">
                      <span className="text-[10px] text-slate-400 block font-mono font-semibold uppercase">Aylıq skan</span>
                      <span className="text-sm font-bold text-indigo-700 font-mono">{(rest.scansThisMonth ?? 0).toLocaleString()}</span>
                    </div>
                    <div className="bg-blue-50/20 p-2.5 rounded-xl border border-blue-100/30">
                      <span className="text-[10px] text-slate-400 block font-mono font-semibold uppercase">Menyudakı təam</span>
                      <span className="text-sm font-bold text-blue-700 font-mono">{rest.totalMenuItems}</span>
                    </div>
                  </div>

                  {/* Custom Domain verification status */}
                  {rest.customDomain ? (
                    <div className="flex items-center gap-1.5 text-xs text-slate-600 font-mono bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200/50">
                      <Globe className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="truncate">{rest.customDomain}</span>
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-400 font-mono italic">
                      qr-menu.ru alt-domenindən istifadə edir
                    </div>
                  )}
                </div>

                {/* Secondary Bottom Controls Panel */}
                <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
                  <span className="text-[10px] text-slate-400 font-mono">
                    Qoşulma tarixi: {rest.createdAt}
                  </span>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingRest(rest);
                        setIsEditOpen(true);
                      }}
                      className="p-1.5 text-slate-600 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                      title="Limitləri tənzimlə"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`"${rest.name}" restoranını CRM sistemindən silməyə əminsiniz?`)) {
                          onDelete(rest.id);
                        }
                      }}
                      className="p-1.5 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                      title="Hesabı sil"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Adding Restaurant modal */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in">
            <div className="p-6 border-b border-indigo-50 bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="font-display font-extrabold text-lg text-slate-900">Yeni Restoranın Qoşulması</h3>
                <p className="text-xs text-slate-500 mt-0.5">Sahibkarın məlumatlarını daxil edin və başlanğıc tarifi aktivləşdirin.</p>
              </div>
              <button 
                onClick={() => setIsAddOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Restoranın adı</label>
                  <input
                    type="text"
                    required
                    placeholder="Məsələn: Sahil Lahmacun"
                    value={newRest.name}
                    onChange={(e) => setNewRest({...newRest, name: e.target.value})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Mətbəx növü</label>
                  <input
                    type="text"
                    required
                    placeholder="Azərbaycan, Türk, İtalyan..."
                    value={newRest.cuisine}
                    onChange={(e) => setNewRest({...newRest, cuisine: e.target.value})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Sahibkarın TAA (Adı, Soyadı)</label>
                <input
                  type="text"
                  required
                  placeholder="Əli Yusifov"
                  value={newRest.ownerName}
                  onChange={(e) => setNewRest({...newRest, ownerName: e.target.value})}
                  className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Sənədləşmə üçün E-mail</label>
                  <input
                    type="email"
                    required
                    placeholder="manager@restaurant.com"
                    value={newRest.ownerEmail}
                    onChange={(e) => setNewRest({...newRest, ownerEmail: e.target.value})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Əlaqə telefonu</label>
                  <input
                    type="text"
                    placeholder="+994 (50) 123-45-67"
                    value={newRest.ownerPhone}
                    onChange={(e) => setNewRest({...newRest, ownerPhone: e.target.value})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Başlanğıc tarif</label>
                  <select
                    value={newRest.plan}
                    onChange={(e) => setNewRest({...newRest, plan: e.target.value as PlanType})}
                    className="w-full border border-slate-200 py-1.5 px-2 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500 bg-white"
                  >
                    <option value="Free">Free / Trial</option>
                    <option value="Standard">Standard</option>
                    <option value="Premium">Premium</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Fərdi domen (istəyə görə)</label>
                  <input
                    type="text"
                    placeholder="domen.az"
                    value={newRest.customDomain}
                    onChange={(e) => setNewRest({...newRest, customDomain: e.target.value})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex gap-2.5 justify-end pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddOpen(false)}
                  className="px-4 py-2 border border-slate-200 hover:border-slate-300 text-slate-700 text-xs rounded-xl font-bold transition-all cursor-pointer"
                >
                  Ləğv et
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-xl font-bold transition-all cursor-pointer"
                >
                  Yadda saxla və Aktivləşdir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Editing limits modal */}
      {isEditOpen && editingRest && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in">
            <div className="p-6 border-b border-indigo-50 bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="font-display font-extrabold text-lg text-slate-900">Restoran Parametrlərinin Redaktəsi</h3>
                <p className="text-xs text-slate-500 mt-0.5">Hesabın vəziyyəti, tarif limiti və skan limitinin incə tənzimlənməsi.</p>
              </div>
              <button 
                onClick={() => { setIsEditOpen(false); setEditingRest(null); }}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Hesabın statusu</label>
                  <select
                    value={editingRest.status}
                    onChange={(e) => setEditingRest({...editingRest, status: e.target.value as any})}
                    className="w-full border border-slate-200 py-1.5 px-2 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500 bg-white"
                  >
                    <option value="active">Aktivdir (Active)</option>
                    <option value="trial">Sınaq müddəti (Trial)</option>
                    <option value="suspended">Bloklanıb / Dondurulub (Suspended)</option>
                  </select>
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Abunəlik tarifi</label>
                  <select
                    value={editingRest.plan}
                    onChange={(e) => setEditingRest({...editingRest, plan: e.target.value as PlanType})}
                    className="w-full border border-slate-200 py-1.5 px-2 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500 bg-white"
                  >
                    <option value="Free">Free</option>
                    <option value="Standard">Standard</option>
                    <option value="Premium">Premium</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Cari aydakı skanlar</label>
                  <input
                    type="number"
                    value={editingRest.scansThisMonth}
                    onChange={(e) => setEditingRest({...editingRest, scansThisMonth: Number(e.target.value)})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Menyudakı ümumi təam sayı</label>
                  <input
                    type="number"
                    value={editingRest.totalMenuItems}
                    onChange={(e) => setEditingRest({...editingRest, totalMenuItems: Number(e.target.value)})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-600 uppercase font-mono">Xüsusi domen</label>
                <input
                  type="text"
                  placeholder="laspezia.online"
                  value={editingRest.customDomain || ''}
                  onChange={(e) => setEditingRest({...editingRest, customDomain: e.target.value || undefined})}
                  className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-650 uppercase font-mono">Abunəlik bitmə tarixi (İİİİ-AA-GG)</label>
                  <input
                    type="text"
                    value={editingRest.expiryDate}
                    onChange={(e) => setEditingRest({...editingRest, expiryDate: e.target.value})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-650 uppercase font-mono">Müəssisənin reytinqi</label>
                  <input
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={editingRest.rating}
                    onChange={(e) => setEditingRest({...editingRest, rating: Number(e.target.value)})}
                    className="w-full border border-slate-200 py-1.5 px-3 text-xs rounded-lg focus:outline-hidden focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex gap-2.5 justify-end pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => { setIsEditOpen(false); setEditingRest(null); }}
                  className="px-4 py-2 border border-slate-200 hover:border-slate-300 text-slate-700 text-xs rounded-xl font-bold transition-all cursor-pointer"
                >
                  Ləğv et
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-xl font-bold transition-all cursor-pointer"
                >
                  Konfiqurasiyanı Yenilə
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
